/**
 * OrderTable
 * 委托订单列表组件
 *
 * @author page
 * @date 2026-04-25
 */
import React, {useCallback, useEffect, useState} from 'react';
import {simulateTradingService} from '../../services/simulateService';
import type {SimulateOrder} from '../../types/simulate';

interface OrderTableProps {
    tradingPair: string;
    onOrderUpdate?: () => void;
    onCountChange?: (count: number) => void;
}

const STATUS_MAP: Record<number, { label: string; className: string }> = {
    0: {label: '待成交', className: 'status-pending'},
    1: {label: '部分成交', className: 'status-partial'},
    2: {label: '已成交', className: 'status-filled'},
    3: {label: '已取消', className: 'status-cancelled'},
    4: {label: '已拒绝', className: 'status-rejected'},
};

const SIDE_MAP: Record<number, string> = {0: '买', 1: '卖'};
const TYPE_MAP: Record<number, string> = {0: '市价', 1: '限价'};

const OrderTable: React.FC<OrderTableProps> = ({tradingPair, onOrderUpdate, onCountChange}) => {
    const [orders, setOrders] = useState<SimulateOrder[]>([]);
    const [loading, setLoading] = useState(false);

    const loadOrders = useCallback(async () => {
        try {
            setLoading(true);
            const res = await simulateTradingService.getOrders();
            const list = res.data?.data || [];
            // 过滤出 PENDING(0), PARTIAL(1) 状态的订单
            const filtered = list.filter(o => o.status === 0 || o.status === 1);
            setOrders(filtered);
            if (onCountChange) {
                onCountChange(filtered.length);
            }
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    }, [onCountChange]);

    useEffect(() => {
        loadOrders();
        const timer = setInterval(loadOrders, 30000);
        return () => clearInterval(timer);
    }, [loadOrders]);

    const handleCancel = async (orderId: number) => {
        try {
            await simulateTradingService.cancelOrder(orderId);
            loadOrders();
            onOrderUpdate?.();
        } catch {
            // ignore
        }
    };

    if (loading && orders.length === 0) {
        return <div className="empty-tip">加载中...</div>;
    }

    if (orders.length === 0) {
        return <div className="empty-tip">暂无委托订单</div>;
    }

    return (
        <table className="simulate-table" style={{width: '100%', borderCollapse: 'collapse'}}>
            <thead>
            <tr>
                <th>交易对</th>
                <th>方向</th>
                <th>类型</th>
                <th>价格</th>
                <th>数量</th>
                <th>已成交</th>
                <th>状态</th>
                <th>操作</th>
            </tr>
            </thead>
            <tbody>
            {orders.map(order => (
                <tr key={order.id}>
                    <td>{order.symbol}</td>
                    <td style={{color: order.side === 0 ? 'var(--pt-bull)' : 'var(--pt-bear)'}}>
                        {SIDE_MAP[order.side] || '-'}
                    </td>
                    <td>{TYPE_MAP[order.type] || '-'}</td>
                    <td className="mono">{order.price || '-'}</td>
                    <td className="mono">{order.quantity}</td>
                    <td className="mono">{order.filledQuantity}</td>
                    <td>{STATUS_MAP[order.status]?.label || '-'}</td>
                    <td>
                        {(order.status === 0 || order.status === 1) && (
                            <button
                                className="btn-ghost"
                                style={{
                                    color: 'var(--pt-bear)',
                                    cursor: 'pointer',
                                    border: 'none',
                                    background: 'none',
                                    fontSize: 12
                                }}
                                onClick={() => handleCancel(order.id)}
                            >
                                撤单
                            </button>
                        )}
                    </td>
                </tr>
            ))}
            </tbody>
        </table>
    );
};

export default OrderTable;
